import type {
	SearchPrimaryCollectionArtworksQuery,
	SearchPrimaryCollectionArtworksQueryVariables,
} from '../../queries/__generated__/searchPrimaryCollectionArtworks.generated';
import { filterByArtist } from '../filterByArtist/filterByArtist';

// The second-phase query has a fixed number of aliased slots; this is how many
// collections one search can preview artworks for.
export const PRIMARY_COLLECTION_SLOTS = 4;

// Rows fetched per collection. Deliberately larger than the number previewed:
// works crediting the searched artist are ranked to the front afterwards, and
// with no surplus there would be nothing to promote from.
export const PRIMARY_COLLECTION_ARTWORKS_FETCH_LIMIT = 12;

const SLOT_KEYS = [
	'collection0',
	'collection1',
	'collection2',
	'collection3',
] as const;

// Unused slots still have to be given a filter, or Directus treats the absent
// filter as "match everything" and returns an unrelated page of artworks.
// The sentinel is a literal slug no record can hold (slugs are lowercase
// alphanumeric and hyphens). It must not be empty or whitespace: those
// serialise to a NUL byte on the wire, which Directus rejects outright with an
// INTERNAL_SERVER_ERROR that fails the whole query, not just that slot.
const MATCH_NOTHING = {
	primary_collection: { slug: { _eq: '__no_such_collection__' } },
};

export const getPrimaryCollectionArtworksVariables = (
	slugs: (string | null | undefined)[],
	limit: number
): SearchPrimaryCollectionArtworksQueryVariables => {
	const usable = slugs.filter((slug): slug is string => Boolean(slug));

	return {
		limit,
		...Object.fromEntries(
			SLOT_KEYS.map((key, index) => [
				key,
				usable[index]
					? { primary_collection: { slug: { _eq: usable[index] } } }
					: MATCH_NOTHING,
			])
		),
	} as SearchPrimaryCollectionArtworksQueryVariables;
};

type SlotArtworks = NonNullable<
	SearchPrimaryCollectionArtworksQuery['collection0']
>;

// Flattens the aliased slots back into one lookup keyed by collection slug, so
// getSections doesn't need to know about the slot mechanism at all, then drops
// each collection's works that don't credit the searched artist.
export const getArtworksByCollectionSlug = (
	data: SearchPrimaryCollectionArtworksQuery | null | undefined,
	artistName = ''
) => {
	const bySlug = new Map<string, SlotArtworks>();

	if (!data) return bySlug;

	SLOT_KEYS.forEach((key) => {
		data[key]?.forEach((artwork) => {
			const slug = artwork?.primary_collection?.slug;
			if (!slug) return;

			const existing = bySlug.get(slug) || [];
			existing.push(artwork);
			bySlug.set(slug, existing);
		});
	});

	bySlug.forEach((artworks, slug) => {
		bySlug.set(slug, filterByArtist(artworks, artistName));
	});

	return bySlug;
};
